import type {
  TaskItem,
  TaskDetail,
  TaskStatusItem,
  TaskTagItem,
  TaskActivity,
  TaskComment,
  TaskFilters,
  TaskSearchResult,
  TaskLinkType,
  ReorderPayload,
  PaginatedResponse,
  ProjectTaskStats,
  BoardSettings,
} from '@hygge/shared';
import { api } from './api';

// ─── Tasks ──────────────────────────────────────────────────────────────

export async function getTasks(
  boardId: string,
  filters?: TaskFilters & { page?: number; limit?: number; sort?: string; order?: string },
): Promise<PaginatedResponse<TaskItem>> {
  const { data } = await api.get<PaginatedResponse<TaskItem>>(
    `/boards/${boardId}/tasks`,
    { params: filters },
  );
  return data;
}

export async function getTask(key: string): Promise<TaskDetail> {
  const { data } = await api.get<TaskDetail>(`/tasks/${key}`);
  return data;
}

export async function createTask(
  boardId: string,
  payload: {
    title: string;
    description?: string;
    statusId?: string;
    priority?: string;
    type?: string;
    assigneeId?: string;
    /** Full assignee set to create with (first id becomes the primary). */
    assigneeIds?: string[];
    labels?: string[];
    tagIds?: string[];
    storyPoints?: number;
    dueDate?: string;
    parentId?: string;
    sprintId?: string;
  },
): Promise<TaskItem> {
  const { data } = await api.post<TaskItem>(`/boards/${boardId}/tasks`, payload);
  return data;
}

export async function updateTask(
  key: string,
  payload: Record<string, unknown>,
): Promise<TaskItem> {
  const { data } = await api.patch<TaskItem>(`/tasks/${key}`, payload);
  return data;
}

export async function uploadTaskAttachment(
  key: string,
  file: File,
): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post<{ url: string }>(`/tasks/${key}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function deleteTask(key: string): Promise<void> {
  await api.delete(`/tasks/${key}`);
}

export async function reorderTasks(payload: ReorderPayload): Promise<{ success: boolean; updated: number }> {
  const { data } = await api.patch<{ success: boolean; updated: number }>('/tasks/reorder', payload);
  return data;
}

// ─── Statuses ───────────────────────────────────────────────────────────

export async function getStatuses(boardId: string): Promise<TaskStatusItem[]> {
  const { data } = await api.get<TaskStatusItem[]>(`/boards/${boardId}/statuses`);
  return data;
}

export async function createStatus(
  boardId: string,
  payload: { name: string; category: string; color: string; sortOrder?: number },
): Promise<TaskStatusItem> {
  const { data } = await api.post<TaskStatusItem>(`/boards/${boardId}/statuses`, payload);
  return data;
}

export async function updateStatus(
  id: string,
  payload: { name?: string; category?: string; color?: string; sortOrder?: number },
): Promise<TaskStatusItem> {
  const { data } = await api.patch<TaskStatusItem>(`/statuses/${id}`, payload);
  return data;
}

export async function deleteStatus(id: string, migrateToStatusId?: string): Promise<void> {
  const params = migrateToStatusId ? { migrateToStatusId } : undefined;
  await api.delete(`/statuses/${id}`, { params });
}

// ─── Stats ────────────────────────────────────────────────────────────

export async function getBoardTaskStats(boardId: string): Promise<ProjectTaskStats> {
  const { data } = await api.get<ProjectTaskStats>(`/boards/${boardId}/task-stats`);
  return data;
}

// ─── Tags ─────────────────────────────────────────────────────────────

export async function getBoardTags(boardId: string): Promise<TaskTagItem[]> {
  const { data } = await api.get<TaskTagItem[]>(`/boards/${boardId}/tags`);
  return data;
}

export async function createTag(
  boardId: string,
  payload: { name: string; color: string },
): Promise<TaskTagItem> {
  const { data } = await api.post<TaskTagItem>(`/boards/${boardId}/tags`, payload);
  return data;
}

export async function updateTag(
  id: string,
  payload: { name?: string; color?: string },
): Promise<TaskTagItem> {
  const { data } = await api.patch<TaskTagItem>(`/tags/${id}`, payload);
  return data;
}

export async function deleteTag(id: string): Promise<void> {
  await api.delete(`/tags/${id}`);
}

// ─── Status Reorder ──────────────────────────────────────────────────

export async function reorderStatuses(
  boardId: string,
  items: Array<{ id: string; sortOrder: number }>,
): Promise<void> {
  await api.patch(`/boards/${boardId}/statuses/reorder`, { items });
}

// ─── Board Settings ──────────────────────────────────────────────────

export async function getBoardSettings(boardId: string): Promise<BoardSettings> {
  const { data } = await api.get<BoardSettings>(`/boards/${boardId}/board-settings`);
  return data;
}

export async function updateBoardSettings(
  boardId: string,
  payload: Partial<BoardSettings>,
): Promise<BoardSettings> {
  const { data } = await api.patch<BoardSettings>(`/boards/${boardId}/board-settings`, payload);
  return data;
}

// ─── Links ──────────────────────────────────────────────────────────────

export async function createTaskLink(
  taskKey: string,
  payload: { targetKey: string; linkType: TaskLinkType },
) {
  const { data } = await api.post(`/tasks/${taskKey}/links`, payload);
  return data;
}

export async function deleteTaskLink(linkId: string): Promise<void> {
  await api.delete(`/task-links/${linkId}`);
}

// ─── Subtasks / Parent ──────────────────────────────────────────────────

export async function setTaskParent(
  taskKey: string,
  parentKey: string | null,
): Promise<TaskItem> {
  const { data } = await api.patch<TaskItem>(`/tasks/${taskKey}/parent`, { parentKey });
  return data;
}

// ─── Search ─────────────────────────────────────────────────────────────

export async function searchTasks(
  boardId: string,
  q: string,
  excludeKey?: string,
): Promise<TaskSearchResult[]> {
  const { data } = await api.get<TaskSearchResult[]>(
    `/boards/${boardId}/tasks/search`,
    { params: { q, excludeKey } },
  );
  return data;
}

// ─── Activity history ─────────────────────────────────────────────────────

export type { TaskActivity };

export async function getTaskActivity(key: string): Promise<TaskActivity[]> {
  const { data } = await api.get<TaskActivity[]>(`/tasks/${key}/activity`);
  return data;
}

// ─── Comments ───────────────────────────────────────────────────────────

export async function getComments(taskKey: string): Promise<TaskComment[]> {
  const { data } = await api.get<TaskComment[]>(`/tasks/${taskKey}/comments`);
  return data;
}

export async function createComment(
  taskKey: string,
  payload: { body: string; parentId?: string; source?: string },
): Promise<TaskComment> {
  const { data } = await api.post<TaskComment>(`/tasks/${taskKey}/comments`, payload);
  return data;
}

export async function updateComment(
  commentId: string,
  body: string,
): Promise<TaskComment> {
  const { data } = await api.patch<TaskComment>(`/comments/${commentId}`, { body });
  return data;
}

export async function deleteComment(commentId: string): Promise<void> {
  await api.delete(`/comments/${commentId}`);
}
